'use client';

import React, { useMemo } from 'react';
import Link from 'next/link';
import {
 Sparkles,
 ArrowRight,
 Shield,
 User,
 Code2,
 LogIn,
 // Citizen features
 FileText,
 MapPin,
 Bell,
 Clock,
 MessageCircle,
 // Officer features
 Mic,
 Search,
 Brain,
 Video,
 ClipboardList,
 Radio,
 // Developer features
 Key,
 BookOpen,
 Webhook,
 Zap,
 Terminal,
} from 'lucide-react';
import { resolveRunbook } from '@/lib/runbook/client';
import { EngageIQLogo, ProductCompanyLockup } from '@/components/brand/EngageIQBrand';

// ============================================================================
// Types
// ============================================================================

interface PortalConfig {
 id: 'citizen' | 'officer' | 'developer';
 title: string;
 tagline: string;
 href: string;
 icon: React.ReactNode;
 color: string;
 accentColor: 'emerald' | 'blue' | 'violet';
 bgGradient: string;
 borderColor: string;
 hoverBorder: string;
 glowColor: string;
 features: PortalFeature[];
 highlights: string[];
}

interface PortalFeature {
 icon: React.ReactNode;
 title: string;
 description: string;
}

// Portal data is defined inside the Home component via useMemo to avoid
// hydration mismatch (React #418) caused by resolveRunbook() returning
// different values during SSR vs client-side rendering.

// Color styles for each accent color
const colorStyles = {
 emerald: {
  gradient: 'from-emerald-900/40 via-emerald-900/20 to-transparent',
  border: 'border-emerald-500/30',
  hoverBorder: 'hover:border-emerald-500/50',
  glow: 'bg-emerald-500/30',
  ring: 'ring-emerald-500/20',
  text: 'text-emerald-400',
  bg: 'bg-emerald-500/10',
  iconBg: 'bg-emerald-500/15',
  accentBorder: 'border-t-emerald-500/60',
 },
 blue: {
  gradient: 'from-blue-900/40 via-blue-900/20 to-transparent',
  border: 'border-blue-500/30',
  hoverBorder: 'hover:border-blue-500/50',
  glow: 'bg-blue-500/30',
  ring: 'ring-blue-500/20',
  text: 'text-blue-400',
  bg: 'bg-blue-500/10',
  iconBg: 'bg-blue-500/15',
  accentBorder: 'border-t-blue-500/60',
 },
 violet: {
  gradient: 'from-violet-900/40 via-violet-900/20 to-transparent',
  border: 'border-violet-500/30',
  hoverBorder: 'hover:border-violet-500/50',
  glow: 'bg-violet-500/30',
  ring: 'ring-violet-500/20',
  text: 'text-violet-400',
  bg: 'bg-violet-500/10',
  iconBg: 'bg-violet-500/15',
  accentBorder: 'border-t-violet-500/60',
 },
};

// ============================================================================
// Main Component
// ============================================================================

export default function Home() {
 const portals: PortalConfig[] = useMemo(() => [
  {
   id: 'citizen',
   title: resolveRunbook('ui-frontend.landing.citizen.title', 'Citizen Portal'),
   tagline: resolveRunbook('ui-frontend.landing.citizen.tagline', 'Report & Track'),
   href: '/citizen',
   icon: <User size={32} />,
   color: 'text-emerald-400',
   accentColor: 'emerald',
   bgGradient: 'from-emerald-500/20 to-emerald-600/5',
   borderColor: 'border-emerald-500/20',
   hoverBorder: 'hover:border-emerald-500/50',
   glowColor: 'bg-emerald-500/20',
   highlights: ['24/7 Access', 'Mobile Friendly', 'No Account Required'],
   features: [
    {
     icon: <FileText className="text-emerald-400" size={20} />,
     title: resolveRunbook('ui-frontend.landing.citizen.feature_reporting_title', 'Easy Reporting'),
     description: resolveRunbook('ui-frontend.landing.citizen.feature_reporting_desc', 'Submit non-emergency incidents with a guided, step-by-step process designed for simplicity'),
    },
    {
     icon: <MapPin className="text-emerald-400" size={20} />,
     title: resolveRunbook('ui-frontend.landing.citizen.feature_location_title', 'Location Services'),
     description: resolveRunbook('ui-frontend.landing.citizen.feature_location_desc', 'Automatically capture GPS coordinates or manually specify addresses with map verification'),
    },
    {
     icon: <Bell className="text-emerald-400" size={20} />,
     title: resolveRunbook('ui-frontend.landing.citizen.feature_status_title', 'Status Updates'),
     description: resolveRunbook('ui-frontend.landing.citizen.feature_status_desc', 'Receive push notifications and email alerts as your report progresses through the system'),
    },
    {
     icon: <MessageCircle className="text-emerald-400" size={20} />,
     title: resolveRunbook('ui-frontend.landing.citizen.feature_chat_title', 'Two-Way Chat'),
     description: resolveRunbook('ui-frontend.landing.citizen.feature_chat_desc', 'Respond to follow-up questions directly in the app with secure messaging'),
    },
   ],
  },
  {
   id: 'officer',
   title: resolveRunbook('ui-frontend.landing.officer.title', 'Officer Portal'),
   tagline: resolveRunbook('ui-frontend.landing.officer.tagline', 'Command & Respond'),
   href: '/login',
   icon: <Shield size={32} />,
   color: 'text-blue-400',
   accentColor: 'blue',
   bgGradient: 'from-blue-500/20 to-blue-600/5',
   borderColor: 'border-blue-500/20',
   hoverBorder: 'hover:border-blue-500/50',
   glowColor: 'bg-blue-500/20',
   highlights: ['AI-Powered', 'Real-Time', 'Secure Access'],
   features: [
    {
     icon: <Mic className="text-blue-400" size={20} />,
     title: resolveRunbook('ui-frontend.landing.officer.feature_voice_title', 'Voice Intelligence'),
     description: resolveRunbook('ui-frontend.landing.officer.feature_voice_desc', 'Real-time transcription and analysis during citizen interactions'),
    },
    {
     icon: <Brain className="text-blue-400" size={20} />,
     title: resolveRunbook('ui-frontend.landing.officer.feature_ai_title', 'AI Insights'),
     description: resolveRunbook('ui-frontend.landing.officer.feature_ai_desc', 'Automatic entity extraction, sentiment analysis, and situational awareness'),
    },
    {
     icon: <Search className="text-blue-400" size={20} />,
     title: resolveRunbook('ui-frontend.landing.officer.feature_search_title', 'Smart Search'),
     description: resolveRunbook('ui-frontend.landing.officer.feature_search_desc', 'RAG-powered semantic search across cases, SOPs, and knowledge bases'),
    },
    {
     icon: <ClipboardList className="text-blue-400" size={20} />,
     title: resolveRunbook('ui-frontend.landing.officer.feature_reports_title', 'Auto Reports'),
     description: resolveRunbook('ui-frontend.landing.officer.feature_reports_desc', 'Generate draft incident reports from call transcripts with AI assistance'),
    },
    {
     icon: <Video className="text-blue-400" size={20} />,
     title: resolveRunbook('ui-frontend.landing.officer.feature_video_title', 'Video Calls'),
     description: resolveRunbook('ui-frontend.landing.officer.feature_video_desc', 'Secure HD video conferencing with citizens and co-responders'),
    },
    {
     icon: <Radio className="text-blue-400" size={20} />,
     title: resolveRunbook('ui-frontend.landing.officer.feature_dispatch_title', 'Dispatch Board'),
     description: resolveRunbook('ui-frontend.landing.officer.feature_dispatch_desc', 'Real-time dispatch management with active call monitoring and priority queues'),
    },
    {
     icon: <Clock className="text-blue-400" size={20} />,
     title: resolveRunbook('ui-frontend.landing.officer.feature_timeline_title', 'Activity Timeline'),
     description: resolveRunbook('ui-frontend.landing.officer.feature_timeline_desc', 'Complete incident chronology with AI-extracted events and timestamps'),
    },
   ],
  },
  {
   id: 'developer',
   title: resolveRunbook('ui-frontend.landing.developer.title', 'Developer Portal'),
   tagline: resolveRunbook('ui-frontend.landing.developer.tagline', 'Build & Integrate'),
   href: '/developers',
   icon: <Code2 size={32} />,
   color: 'text-violet-400',
   accentColor: 'violet',
   bgGradient: 'from-violet-500/20 to-violet-600/5',
   borderColor: 'border-violet-500/20',
   hoverBorder: 'hover:border-violet-500/50',
   glowColor: 'bg-violet-500/20',
   highlights: ['REST APIs', 'Webhooks', 'Full Docs'],
   features: [
    {
     icon: <Key className="text-violet-400" size={20} />,
     title: 'API Access',
     description: 'RESTful APIs for incidents, search, reporting, and user management',
    },
    {
     icon: <BookOpen className="text-violet-400" size={20} />,
     title: 'Documentation',
     description: 'Interactive API docs with examples, guides, and tutorials',
    },
    {
     icon: <Webhook className="text-violet-400" size={20} />,
     title: 'Webhooks',
     description: 'Real-time event notifications for system integrations',
    },
    {
     icon: <Terminal className="text-violet-400" size={20} />,
     title: 'SDKs & Libraries',
     description: 'Official client libraries for Python, JavaScript, Go, and more',
    },
    {
     icon: <Zap className="text-violet-400" size={20} />,
     title: 'AI Sandbox',
     description: 'Test AI models and prompts in a safe isolated environment',
    },
   ],
  },
 ], []);

 return (
  <main className="min-h-screen bg-[#0a0a0a] text-white selection:bg-blue-500/30 overflow-x-hidden">
   {/* Landing Header */}
   <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0a0a0a]/80 border-b border-white/5">
    <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
     <Link href="/" className="flex items-center">
      <EngageIQLogo
       theme="dark"
       width={140}
       height={38}
       priority
       className="max-h-7 w-auto h-auto"
      />
     </Link>
     <Link
      href="/login"
      className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white/80 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all duration-200"
     >
      <LogIn size={15} />
      <span>Sign In</span>
     </Link>
    </div>
   </header>

   {/* Hero Section */}
   <section className="relative px-4 pt-12 pb-8 md:pt-20 md:pb-12">
    {/* Background Gradients */}
    <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-600/10 rounded-full blur-[128px] pointer-events-none" />
    <div className="absolute top-0 right-1/4 w-96 h-96 bg-violet-600/10 rounded-full blur-[128px] pointer-events-none" />
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-600/10 rounded-full blur-[128px] pointer-events-none" />

    <div className="relative z-10 w-full text-center space-y-6">
     <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-white/70 backdrop-blur-md">
      <Sparkles size={14} className="text-amber-400" />
      <span suppressHydrationWarning>{resolveRunbook('ui-frontend.landing.badge_text', 'AI-Enhanced Public Safety Platform')}</span>
     </div>

     <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight">
      <span className="bg-clip-text text-transparent bg-gradient-to-b from-white to-white/60">
       Welcome to
      </span>
      <br />
      <EngageIQLogo
       theme="dark"
       width={400}
       height={109}
       priority
       className="inline-block mt-2 md:mt-4 max-h-[80px] w-auto h-auto"
      />
     </h1>

     <p className="max-w-2xl mx-auto text-lg md:text-xl text-zinc-400 leading-relaxed" suppressHydrationWarning>
      {resolveRunbook('ui-frontend.landing.hero_tagline', 'A unified platform connecting communities, responders, and technology for safer neighborhoods.')}
     </p>
    </div>
   </section>

   {/* Portal Entry Cards — Officer is larger center column */}
   <section className="relative px-4 py-8">
    <div className="max-w-6xl mx-auto">
     <div className="grid grid-cols-1 md:grid-cols-[1fr_1.3fr_1fr] gap-4 md:gap-5 items-start">
      {portals.map((portal) => (
       <PortalEntryCard key={portal.id} portal={portal} />
      ))}
     </div>
    </div>
   </section>

   {/* Platform Features — Unified card per portal */}
   <section className="relative px-4 py-12 md:py-16">
    {/* Background subtle gradients */}
    <div className="absolute left-0 top-1/4 w-96 h-96 bg-emerald-600/5 rounded-full blur-[200px] pointer-events-none" />
    <div className="absolute left-1/2 -translate-x-1/2 top-1/2 w-96 h-96 bg-blue-600/5 rounded-full blur-[200px] pointer-events-none" />
    <div className="absolute right-0 top-3/4 w-96 h-96 bg-violet-600/5 rounded-full blur-[200px] pointer-events-none" />

    <div className="relative z-10 max-w-6xl mx-auto">
     {/* Section Header */}
     <div className="text-center mb-12">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-3">What&apos;s included</p>
      <h2 className="text-2xl md:text-3xl font-bold text-white mb-3" suppressHydrationWarning>
       {resolveRunbook('ui-frontend.landing.section_features_title', 'Platform Features')}
      </h2>
      <div className="mx-auto w-12 h-0.5 rounded-full bg-gradient-to-r from-emerald-500 via-blue-500 to-violet-500 mt-4" />
     </div>

     {/* Three Column Feature Grid */}
     <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-5">
      {portals.map((portal) => (
       <PortalFeatureColumn key={portal.id} portal={portal} />
      ))}
     </div>
    </div>
   </section>

   {/* Bottom CTA Section */}
   <section className="relative px-4 py-16 md:py-24">
    <div className="text-center space-y-6">
     <h2 className="text-2xl md:text-3xl font-semibold text-white/90" suppressHydrationWarning>
      {resolveRunbook('ui-frontend.landing.cta_title', 'Ready to get started?')}
     </h2>
     <p className="text-zinc-400 text-lg" suppressHydrationWarning>
      {resolveRunbook('ui-frontend.landing.cta_desc', 'Select a portal above to access the platform, or learn more about our technology.')}
     </p>
     <div className="flex flex-wrap justify-center gap-4 pt-4">
      <Link
       href="https://neutralaiz.com/features"
       className="px-6 py-3 rounded-full border border-white/10 hover:bg-white/5 transition-all duration-300 backdrop-blur-sm text-zinc-400 hover:text-white text-sm"
      >
       Platform Features
      </Link>
      <Link
       href="https://neutralaiz.com/about"
       className="px-6 py-3 rounded-full border border-white/10 hover:bg-white/5 transition-all duration-300 backdrop-blur-sm text-zinc-400 hover:text-white text-sm"
      >
       About EngageIQ
      </Link>
     </div>
    </div>
   </section>

   {/* Footer */}
   <footer className="border-t border-white/5 px-4 py-8">
    <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-zinc-500">
     <ProductCompanyLockup productHeight={20} companyHeight={10} />
     <div className="flex flex-wrap justify-center gap-6">
      <Link href="/privacy" className="hover:text-white transition-colors">Privacy</Link>
      <Link href="/terms" className="hover:text-white transition-colors">Terms</Link>
      <Link href="/accessibility" className="hover:text-white transition-colors">Accessibility</Link>
      <Link href="/help" className="hover:text-white transition-colors">Help</Link>
     </div>
    </div>
   </footer>
  </main>
 );
}

// ============================================================================
// Portal Entry Card Component
// ============================================================================

function PortalEntryCard({ portal }: { portal: PortalConfig }) {
 const isOfficer = portal.id === 'officer';

 return (
  <Link
   href={portal.href}
   className={`
    group relative rounded-2xl
    bg-gradient-to-b ${portal.bgGradient}
    border ${portal.borderColor} ${portal.hoverBorder}
    backdrop-blur-sm transition-all duration-300
    hover:scale-[1.02] hover:shadow-xl
    flex flex-col items-center text-center
    ${isOfficer
     ? 'p-8 md:p-10 md:-mt-2 md:mb-[-8px] shadow-lg shadow-blue-500/10 border-blue-500/30'
     : 'p-6 md:p-8'
    }
   `}
  >

   {/* Icon */}
   <div className={`
    mb-4 rounded-xl bg-white/5
    group-hover:scale-110 group-hover:bg-white/10
    transition-all duration-300 ${portal.color}
    ${isOfficer ? 'p-5' : 'p-4'}
   `}>
    {isOfficer
     ? React.cloneElement(portal.icon as React.ReactElement<{ size?: number }>, { size: 40 })
     : portal.icon
    }
   </div>

   {/* Title & Tagline */}
   <h2 className={`font-bold text-white mb-1 ${isOfficer ? 'text-2xl md:text-3xl' : 'text-xl md:text-2xl'}`} suppressHydrationWarning>
    {portal.title}
   </h2>
   <p className={`font-medium ${portal.color} mb-3 ${isOfficer ? 'text-base' : 'text-sm'}`} suppressHydrationWarning>
    {portal.tagline}
   </p>

   {/* Highlight Pills */}
   <div className="flex flex-wrap justify-center gap-2 mb-4">
    {portal.highlights.map((highlight, idx) => (
     <span
      key={idx}
      className="px-2 py-0.5 text-xs rounded-full bg-white/5 text-zinc-400 border border-white/5"
     >
      {highlight}
     </span>
    ))}
   </div>

   {/* Enter Button */}
   <div className={`
    mt-auto rounded-full
    bg-white/5 border border-white/10
    group-hover:bg-white/10 group-hover:border-white/20
    transition-all duration-300
    flex items-center gap-2 text-white/80
    ${isOfficer ? 'px-6 py-2.5 text-sm font-medium' : 'px-5 py-2 text-sm'}
   `}>
    <span>Enter Portal</span>
    <ArrowRight
     size={16}
     className="group-hover:translate-x-1 transition-transform duration-300"
    />
   </div>
  </Link>
 );
}

// ============================================================================
// Portal Feature Column — Unified card with integrated rows
// ============================================================================

function PortalFeatureColumn({ portal }: { portal: PortalConfig }) {
 const styles = colorStyles[portal.accentColor];

 return (
  <div className={`
   rounded-xl overflow-hidden
   border border-white/[0.06]
   bg-white/[0.02]
   border-t-2 ${styles.accentBorder}
   flex flex-col
  `}>
   {/* Card Header */}
   <div className={`px-5 py-4 bg-gradient-to-br ${styles.gradient}`}>
    <div className="flex items-center gap-3">
     <div className={`p-2 rounded-lg ${styles.iconBg}`}>
      <div className={styles.text}>
       {React.cloneElement(portal.icon as React.ReactElement<{ size?: number }>, { size: 22 })}
      </div>
     </div>
     <div>
      <h3 className="font-semibold text-white text-sm" suppressHydrationWarning>{portal.title}</h3>
      <p className={`text-xs ${styles.text}`} suppressHydrationWarning>{portal.tagline}</p>
     </div>
    </div>
   </div>

   {/* Feature Rows */}
   <div className="flex-1">
    {portal.features.map((feature, idx) => (
     <div
      key={idx}
      className={`
       group px-5 py-3.5 flex items-start gap-3
       hover:bg-white/[0.03] transition-colors duration-200
       ${idx < portal.features.length - 1 ? 'border-b border-white/[0.04]' : ''}
      `}
     >
      <div className={`flex-shrink-0 p-1.5 rounded-md ${styles.iconBg} mt-0.5`}>
       {feature.icon}
      </div>
      <div className="min-w-0">
       <h4 className="font-medium text-white/90 text-sm leading-tight" suppressHydrationWarning>
        {feature.title}
       </h4>
       <p className="text-xs text-zinc-500 leading-relaxed mt-0.5" suppressHydrationWarning>
        {feature.description}
       </p>
      </div>
     </div>
    ))}
   </div>

   {/* Card Footer — Open Portal */}
   <Link
    href={portal.href}
    className={`
     px-5 py-3.5 border-t border-white/[0.04]
     flex items-center justify-center gap-2
     text-sm font-medium text-white/60 hover:text-white
     hover:bg-white/[0.03] transition-all duration-200
    `}
   >
    <span>Open {portal.title.split(' ')[0]} Portal</span>
    <ArrowRight size={14} />
   </Link>
  </div>
 );
}
